import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AvailabilityComponent } from '@app/shared/components/availability/availability-text.component';
import { MenuOverlayComponent } from '@app/shared/components/menu-overlay/menu-overlay';
import { ComponentVariantFromApiModel } from '@models/api-models/component-variant-fromapi';
import { ModelModel } from '@models/api-models/model';
import { UserDataModel } from '@models/user-data-model';
import { VariantColorModel } from '@models/variant-color';
import { TranslatePipe } from '@ngx-translate/core';
import { DataTranslatePipe } from '@pipes/data-translate.pipe';
import { ReplaceHashPipe } from '@pipes/replace-hash.pipe';
import { ImageService } from '@services/image.service';
import { LanguageService } from '@services/language.service';
import { SorterService } from '@services/sorter.service';
import { UserTypeConstant } from '@shared/user-type-constants';

@Component({
	selector: 'select-box-item',
	templateUrl: 'select-box-item.html',
	styleUrls: ['./select-box-item.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, AvailabilityComponent, TranslatePipe, DataTranslatePipe, ReplaceHashPipe, MenuOverlayComponent]
})
export class SelectBoxItemComponent {

	@Input() selected: boolean;
	@Input() option: any;
	@Input() editMode: boolean;
	@Input() optionsDiscontinued: string;
	@Input() optionsText: string;
	@Input() optionsDescription2: string;
	@Input() optionsImage?: (option: any, p: string | null) => string;
	@Input() imageClass: string;
	@Input() optionsCaption?: string;
	@Input() new: boolean;
	@Input() colorMatchMode: boolean;
	@Input() colorsAvailableForMatching: Array<VariantColorModel> | null = null;
	@Input() currentColor?: VariantColorModel | null;
	@Input() variantOrModelIsNew: boolean = false;
	@Input() matchColor: number;
	@Input() isAccordion: boolean = true;
	@Input() hasSubItems: boolean;

	@Input() user: UserDataModel | null;

	@Output() matchColorClicked: EventEmitter<null> = new EventEmitter();

	public hasVariantsOfSameColor: boolean | null;
	public showOverlay: boolean = false;
	public mainOverlayText: string;
	public secondOverlayText: string;

	userTypeConstant = UserTypeConstant;

	constructor(
		public imageService: ImageService,
		public sorterService: SorterService,
		public translateService: LanguageService,
	) { }

	public hasMatchableColors(variantFromApi: ComponentVariantFromApiModel): boolean | null {
		if (!this.colorsAvailableForMatching) {
			return null;
		}

		const nextColor = ComponentVariantFromApiModel.GetNextColor(this.currentColor, variantFromApi.VariantSizes.selectMany(size => size.VariantColors));

		if (nextColor) {
			return this.colorsAvailableForMatching.some(clr => clr?.Color?.IsAlike(nextColor.Color));
		}

		return false;
	}

	public replaceHash(input: string): string {
		return input.replace('#', `□`).replace('¤', `○`);
	}

	public showSizes(sizes: Array<string>): string {
		for (let i = 0; i < sizes.length; i++) {
			sizes[i] = sizes[i].replace(/#.*$/, '').replace(/¤.*$/, '');
		}

		return sizes.sort((a, b) => SorterService.sortByFunc(a, b)).join('/');
	}

	onClicked() {
		if (!(this.option instanceof ModelModel)) {
			this.hasVariantsOfSameColor = this.hasMatchableColors(this.option);

			if (this.hasVariantsOfSameColor) {
				if (this.matchColor === 1) {
					this.matchColorClicked.emit();
				}
				else if (this.matchColor === 2) {
					this.showOverlay = true;
					this.mainOverlayText = this.translateService.instant('CUSTOMIZER.COLOR.MATCHCOLOR');

					new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
						this.showOverlay = false;
					});
				}
			}

			if (!this.hasVariantsOfSameColor && this.selected) { // doubleclick
				if (this.matchColor === 1) {
					this.matchColorClicked.emit();
				}
				else if (this.matchColor === 2) {
					this.showOverlay = true;
					this.secondOverlayText = this.translateService.instant('CUSTOMIZER.COLOR.NOMATCHCOLOR');

					new Promise(resolve => setTimeout(resolve, 1500)).then(x => {
						this.showOverlay = false;
					});
				}
			}
			else if (this.editMode) {
				this.showOverlay = true;
				this.secondOverlayText = this.translateService.instant('CUSTOMIZER.CHANGES.BAGUPDATED');

				new Promise(resolve => setTimeout(resolve, 2500)).then(x => {
					this.showOverlay = false;
				});
			}
		}
	}

	onColorMatchClicked() {
		this.matchColorClicked.emit();
		this.showOverlay = false;
	}

}
